import {
  FC,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'components/Button';
import ChangeProfileBox from 'components/ChangeProfileBox';
import ProfileBox from 'components/ProfileBox';
import useUser from 'api/hooks/useUser';

import editSvg from '/edit.svg';
import deleteProfileSvg from '/deleteProfile.svg';
import cancelSvg from '/cancelIcon.svg';

import useCountrySettings from 'api/hooks/useCountrySettings';
import profileMutation from 'api/mutations/profileMutation';
import Modal from 'components/Modal';
import deleteAccountMutation from 'api/mutations/deleteAccountMutation';
import { successToast } from 'utils/toast';

interface SelectProps {
  value: string;
  label: string;
}

interface BlueProfileBoxProps {
  title: string;
}

const Profile = () => {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const { data: userSettings } = useCountrySettings();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { mutate } = profileMutation();
  const [blLanguage, setBlLanguage] = useState<SelectProps>({
    value: user?.blueLang,
    label: user?.blueLang,
  });

  const [grLanguage, setGrLanguage] = useState<SelectProps>({
    value: user?.greenLang,
    label: user?.greenLang,
  });
  const [blCurrency, setBlCurrency] = useState<SelectProps | null>(null);
  const [grCurrency, setGrCurrency] = useState<SelectProps | null>(null);
  const [modal, setModal] = useState<boolean>(false);
  const { mutate: deleteAccountMutate } = deleteAccountMutation();
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);

  const systemMetric = localStorage.getItem('metric');

  const [metric, setMetric] = useState(systemMetric ?? 'Metric system');

  useEffect(() => {
    if (!systemMetric) {
      localStorage.setItem('metric', 'Metric system');
    }
  }, [systemMetric]);

  const optionsSettingsBlueLang = userSettings?.blueLang?.map(
    (blueLang: string) => ({
      value: blueLang[0],
      label: blueLang[0]
    }),
  );

  const optionsSettingsGreenLang = userSettings?.greenLang?.map(
    (greenLang: string) => ({
      value: greenLang[0],
      label: greenLang[0]
    }),
  );

  const optionsCurr = userSettings?.currencies?.map((curr: string) => ({
    value: curr[0],
    label: curr[0],
  }));

  const profileChange = () => {
    mutate(
      {
        blueLang: blLanguage ? blLanguage.value : user?.blueLang,
        greenLang: grLanguage ? grLanguage.value : user?.greenLang,
        blueCurrency: {
          code: blCurrency ? blCurrency.value : user?.blueCurrency?.code,
        },
        greenCurrency: {
          code: grCurrency ? grCurrency.value : user?.greenCurrency?.code,
        },
      },
      {
        onSuccess() {
          setIsEditing(false);
          setBlCurrency(null);
          setGrCurrency(null);
        },
      },
    );
    localStorage.setItem('metric', metric);
  };

  const deleteAccount = () => {
    deleteAccountMutate(null, {
      onSuccess() {
        successToast(t('accountDeletedSuccess'));
        localStorage.clear();
        window.location.reload();
      },
    });
  };

  const onCancelWithoutSave = () => {
    if (
      (blCurrency && blCurrency !== user?.blueCurrency?.code) ||
      (grCurrency && grCurrency !== user?.greenCurrency?.code) ||
      (blLanguage && blLanguage !== user?.blueLang) ||
      (grLanguage && grLanguage !== user?.greenLang)
    ) {
      setModal(true);
    } else {
      setIsEditing(false);
    }
  };

  const disableSaveBtn = () => {
    if (
      (blCurrency && blCurrency !== user?.blueCurrency?.code) ||
      (grCurrency && grCurrency !== user?.greenCurrency?.code) ||
      (blLanguage && blLanguage !== user?.blueLang) ||
      (grLanguage && grLanguage !== user?.greenLang) ||
      systemMetric !== metric
    ) {
      return false;
    } else {
      return true;
    }
  };

  const lightBlueUser = user?.role?.id === 6;

  const EditableProfileBoxGreen = () => <ChangeProfileBox
    title={`${t('green')} ${t('app')}`}
    metric={metric}
    setMetric={setMetric}
    btn={
      <Button
        className="bg-primitiveGreen border-green border text-green text-xs h-8"
        size="sm"
      >
        {t('green')}
      </Button>
    }
    disable={{
      currency: true,
      language: true,
    }}
    lng={grLanguage?.value}
    currency={user?.greenCurrency}
    optionsCurr={optionsCurr}
    optionsLng={optionsSettingsGreenLang}
    setLanguage={setGrLanguage}
    setCurrency={setGrCurrency}
  />;
  const EditableProfileBoxBlue: FC<BlueProfileBoxProps> = ({ title }) => <ChangeProfileBox
    title={`${title} ${t('app')}`}
    metric={metric}
    setMetric={setMetric}
    btn={
      <Button
        size="sm"
        className="bg-primitiveBlue border-primary border text-primary text-xs h-8"
      >
        {title}
      </Button>
    }
    lng={blLanguage?.value}
    currency={user?.blueCurrency}
    optionsCurr={optionsCurr}
    optionsLng={optionsSettingsBlueLang}
    setLanguage={setBlLanguage}
    setCurrency={setBlCurrency}
  />;
  const ProfileBoxGreen = () => <ProfileBox
    title={`${t('green')} ${t('app')}`}
    btn={
      <Button
        className="bg-primitiveGreen border-green border text-green text-xs h-8"
        size="sm"
      >
        {t('green')}
      </Button>
    }
    lng={grLanguage?.value}
    currency={user?.greenCurrency}
  />;
  const ProfileBoxBlue: FC<BlueProfileBoxProps> = ({ title }) => <ProfileBox
    title={`${title} ${t('app')}`}
    btn={
      <Button
        size="sm"
        className="bg-primitiveBlue border-primary border text-primary text-xs h-8"
      >
        {title}
      </Button>
    }
    lng={blLanguage?.value}
    currency={user?.blueCurrency}
  />;

  return (
    <>
      <div className="flex flex-col px-56 py-14 md:px-1 md:py-3">
        {modal && (
          <Modal
            saveBtnText={t('save')}
            cancelBtnText={t('cancel')}
            title={t('confirmation')}
            text={t('unsavedChanges')}
            onCancelClick={() => {
              setModal(false);
              setIsEditing(false);
              setBlCurrency(null);
              setGrCurrency(null);
              setBlLanguage(user?.blueLang);
              setGrLanguage(user?.greenLang);
            }}
            onSaveClick={() => setModal(false)}
          />
        )}
        {isEditing && (
          <div className="hidden md:flex md:justify-between md:items-center md:m-0 md:p-0 md:mb-2 md:mr-3">
            <div />
            <div className="font-medium text-lg md:font-normal">
              Edit profile
            </div>
            <div className="cursor-pointer" onClick={onCancelWithoutSave}>
              <img src={cancelSvg} alt="cancel" />
            </div>
          </div>
        )}
        {isEditing && (
          <div className="hidden md:block h-px bg-[#E2E2E2] w-full mx-3 my-4 md:m-0" />
        )}
        <div className="mb-12 flex items-start justify-between md:m-2">
          <div className="flex items-center md:items-start">
            <div className="flex items-center justify-center bg-primary h-24 w-24 rounded-full text-2xl text-grey mr-6 md:w-16 md:h-16">
              {user?.firstName?.charAt(0)}
              {user?.lastName?.charAt(0)}
            </div>
            <div className="flex flex-col">
              <div className="flex items-center md:flex-col md:items-start">
                <div className="text-2xl font-bold md:text-lg">
                  {user?.firstName} {user?.lastName} &nbsp;
                </div>
                <div className="text-secondaryText">
                  <span className="md:hidden">| &nbsp;</span> {t('distributor')}
                </div>
              </div>
              <div className="text-secondaryText my-2 md:my-1">
                {user?.email}
              </div>
              <div className="flex">
                {user?.role?.id === 3 && (
                  <Button
                    size="sm"
                    className="bg-primitiveBlue border-primary border text-primary text-xs h-8 mr-3"
                  >
                    {t('blue')}
                  </Button>
                )}
                {lightBlueUser && (
                  <Button
                    size="sm"
                    className="bg-primitiveBlue border-primary border text-primary text-xs h-8 mr-3"
                  >
                    {t('blueLight')}
                  </Button>
                )}
                {user?.role?.id === 4 && (
                  <Button
                    className="bg-primitiveGreen border-green border text-green text-xs h-8"
                    size="sm"
                  >
                    {t('green')}
                  </Button>
                )}
                {(user?.role?.id === 1 || user?.role?.id === 5) && (
                  <>
                    <Button
                      size="sm"
                      className="bg-primitiveBlue border-primary border text-primary text-xs h-8  mr-3"
                    >
                      {t('blue')}
                    </Button>
                    <Button
                      className="bg-primitiveGreen border-green border text-green text-xs h-8"
                      size="sm"
                    >
                      {t('green')}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
          {localStorage.getItem('netStatus') !== 'offline' && (
            <div>
              {isEditing ? (
                <div className="flex md:hidden">
                  <Button
                    onClick={onCancelWithoutSave}
                    className="w-28 mr-4"
                    btnType="secondary"
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={disableSaveBtn()}
                    className="w-28"
                    onClick={profileChange}
                  >
                    Save
                  </Button>
                </div>
              ) : (
                <>
                  <Button
                    onClick={() => setIsEditing(true)}
                    className="md:hidden"
                    btnType="secondary"
                  >
                    Edit profile
                  </Button>
                  <div
                    onClick={() => setIsEditing(true)}
                    className="hidden md:block md:cursor-pointer"
                  >
                    <img src={editSvg} alt="edit" />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        {isEditing
          ? (
            <div className="grid gap-8 grid-cols-2 grid-rows-1 md:grid-cols-1 md:grid-rows-1 md:mx-2 md:mt-2">
              {user?.role?.id === 3 && <EditableProfileBoxBlue title={t('blue')} />}
              {lightBlueUser && <EditableProfileBoxBlue title={t('blueLight')} />}
              {user?.role?.id === 4 && <EditableProfileBoxGreen />}
              {(user?.role?.id === 1 || user?.role?.id === 5) && (
                <>
                  <EditableProfileBoxBlue title={t('blue')} />
                  <EditableProfileBoxGreen />
                </>
              )}
            </div>
          )
          : (
            <div className="grid gap-8 grid-cols-2 grid-rows-1 md:grid-cols-1 md:grid-rows-1 md:mx-2 md:mt-2">
              {user?.role?.id === 3 && <ProfileBoxBlue title={t('blue')} />}
              {lightBlueUser && <ProfileBoxBlue title={t('blueLight')} />}
              {user?.role?.id === 4 && <ProfileBoxGreen />}
              {(user?.role?.id === 1 || user?.role?.id === 5) && (
                <>
                  <ProfileBoxBlue title={t('blue')} />
                  <ProfileBoxGreen />
                </>
              )}
            </div>
          )}

        {confirmDelete && (
          <Modal
            saveBtnText={t('delete')}
            cancelBtnText={t('cancel')}
            title={t('confirmation')}
            text={t('confirmDelete')}
            onCancelClick={() => setConfirmDelete(false)}
            onSaveClick={deleteAccount}
          />
        )}

        {isEditing && (
          <div className="hidden md:block md:m-2">
            <Button
              disabled={disableSaveBtn()}
              onClick={profileChange}
              className="w-full mt-5"
            >
              {t('save')}
            </Button>
          </div>
        )}
        {isEditing && (
          <div className="flex items-center justify-center my-4">
            <div
              className="cursor-pointer flex items-center"
              onClick={() => setConfirmDelete(true)}
            >
              <img src={deleteProfileSvg} alt="" />
              <div className="text-red500 ml-2">{t('deleteAccount')}</div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Profile;
